"use client";

import { Component, type CSSProperties, type ReactNode } from "react";
import Markdown, { type Components } from "react-markdown";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";
import renderInlineMarkdown from "@/utils/renderInlineMarkdown";

/**
 * Renders an assistant reply as markdown — the subset the backend commits to
 * emitting: pipe tables with column alignment, bullet and numbered lists, and
 * inline bold / italic / code.
 *
 * Raw HTML is deliberately NOT enabled (no `rehype-raw`, no
 * `dangerouslySetInnerHTML`): this string is model output, so HTML stays
 * escaped rather than rendered.
 *
 * `remarkBreaks` is not optional. Markdown treats a single newline as a soft
 * break, so without it every newline-separated reply — including every reply
 * already stored from before this change — collapses into one run-on
 * paragraph. It is also why the CALLER must not set `whitespace-pre-line`: the
 * plugin emits a real <br/> followed by a literal newline, and pre-line would
 * render that newline as a second break.
 */
const PLUGINS = [remarkGfm, remarkBreaks];

/** Elements outside the agreed subset degrade to their text, never to raw markup. */
const COMPONENTS: Components = {
  p: ({ children }: { children?: ReactNode }) => (
    <p className="mb-2 last:mb-0">{children}</p>
  ),
  ul: ({ children }: { children?: ReactNode }) => (
    <ul className="mb-2 list-disc space-y-1 pl-5 last:mb-0">{children}</ul>
  ),
  ol: ({ children }: { children?: ReactNode }) => (
    <ol className="mb-2 list-decimal space-y-1 pl-5 last:mb-0">{children}</ol>
  ),
  li: ({ children }: { children?: ReactNode }) => <li>{children}</li>,
  strong: ({ children }: { children?: ReactNode }) => (
    <strong className="font-semibold">{children}</strong>
  ),
  em: ({ children }: { children?: ReactNode }) => <em>{children}</em>,
  code: ({ children }: { children?: ReactNode }) => (
    <code className="font-geist-mono text-[0.9em]">{children}</code>
  ),
  // A wide table must scroll INSIDE the bubble — the page itself must never
  // scroll sideways. This is what makes the 478px Ask-AI popover workable.
  // `overflow-auto` + `max-h` gives it BOTH axes: wide tables scroll sideways,
  // long ones scroll vertically instead of stretching the reply off-screen.
  // `ai-table-scroll` makes the bars visible — globals.css hides every WebKit
  // scrollbar app-wide, which would leave the overflow undiscoverable.
  table: ({ children }: { children?: ReactNode }) => (
    <div className="ai-table-scroll my-2 max-h-[360px] max-w-full overflow-auto">
      {/* `w-max min-w-full`: at least fill the bubble, but take the natural
          content width when the columns need more — that overflow is what the
          wrapper scrolls. Plain `w-full` would fight the cells' nowrap. */}
      <table className="w-max min-w-full border-collapse text-left">
        {children}
      </table>
    </div>
  ),
  thead: ({ children }: { children?: ReactNode }) => (
    <thead className="border-stroke-soft-200 border-b">{children}</thead>
  ),
  tbody: ({ children }: { children?: ReactNode }) => <tbody>{children}</tbody>,
  tr: ({ children }: { children?: ReactNode }) => (
    <tr className="border-stroke-soft-200 border-b last:border-b-0">
      {children}
    </tr>
  ),
  // `style` carries remark-gfm's column alignment (text-align from the |---:| marker).
  th: ({
    children,
    style,
  }: {
    children?: ReactNode;
    style?: CSSProperties;
  }) => (
    <th
      // `border-r` rules the columns apart; `last:border-r-0` keeps the table's
      // right edge clean instead of drawing a line against the bubble wall.
      className="whitespace-nowrap border-stroke-soft-200 border-r px-3 py-1.5 font-semibold last:border-r-0"
      style={style}
    >
      {children}
    </th>
  ),
  td: ({
    children,
    style,
  }: {
    children?: ReactNode;
    style?: CSSProperties;
  }) => (
    <td
      // Same column rule as the header — see `th`.
      className="whitespace-nowrap border-stroke-soft-200 border-r px-3 py-1.5 last:border-r-0"
      style={style}
    >
      {children}
    </td>
  ),
  // Links are outside the subset; render the label so a stray one is not a dead
  // blue control the user can click into nowhere.
  a: ({ children }: { children?: ReactNode }) => <>{children}</>,
};

export function AiMarkdown({ text }: { text: string }) {
  return (
    <Markdown remarkPlugins={PLUGINS} components={COMPONENTS}>
      {text}
    </Markdown>
  );
}

interface BoundaryProps {
  text: string;
}

interface BoundaryState {
  failed: boolean;
}

/**
 * Falls back to the plain inline renderer if markdown rendering throws, so a
 * parse fault degrades one reply's formatting instead of blanking the thread.
 *
 * The fallback is deliberately STICKY for the life of this message. Clearing
 * the flag on new props would re-enter the renderer that just threw, which
 * throws again on the next frame — an infinite error loop rather than a
 * recovery. A message that failed once stays plain; the next message mounts a
 * fresh boundary. react-markdown does not throw on malformed markdown, so this
 * is a backstop, not a routine path.
 */
export class AiMarkdownBoundary extends Component<
  BoundaryProps,
  BoundaryState
> {
  state: BoundaryState = { failed: false };

  static getDerivedStateFromError(): BoundaryState {
    return { failed: true };
  }

  render() {
    if (this.state.failed) return <>{renderInlineMarkdown(this.props.text)}</>;
    return <AiMarkdown text={this.props.text} />;
  }
}
